import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
    CreateDnsRecordRequest,
    DeleteDnsRecordRequest,
    DomainDetails,
    DomainsService,
    InternalDnsRecord,
    UpdateDnsRecordRequest,
} from '@app/modules/domains/domains.service';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

// Use InternalDnsRecord type for our component since it includes uuid
type DnsRecord = InternalDnsRecord;

// Import the component we are adding to the template

@Component({
  selector: 'app-domain-detail',
  templateUrl: './domain-detail.component.html',
  styles: [
    `
      p {
        margin: 0 !important;
      }

      button[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `,
  ],
})
export class DomainDetailComponent implements OnInit {
  domainUuid: string;
  domain: DomainDetails | null = null;
  loading = true;
  loadingDns = true;
  editingRecordId: number | null = null;
  editingRecord: DnsRecord | null = null;
  isAddingNewRecord = false;
  recordForm: FormGroup;
  showAddRecordForm = false;
  savingRecord = false;
  deletingRecord = false;

  recordTypes = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA'];
  ttlOptions = [
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' },
    { value: 3600, label: '1 hour' },
    { value: 86400, label: '1 day' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private toast: ToastNotificationsService,
    private domainsService: DomainsService
  ) {
    this.recordForm = this.fb.group({
      type: ['A', Validators.required],
      name: [''],
      ttl: [3600, Validators.required],
      content: ['', Validators.required],
      priority: [null],
    });

    // Add conditional validation for priority field
    this.recordForm.get('type')?.valueChanges.subscribe((type) => {
      const priorityControl = this.recordForm.get('priority');
      if (type === 'MX' || type === 'SRV') {
        priorityControl?.setValidators([Validators.required]);
      } else {
        priorityControl?.clearValidators();
      }
      priorityControl?.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.domainUuid = this.route.snapshot.paramMap.get('uuid') || '';
    this.loadDomainDetails();
  }

  loadDomainDetails(): void {
    this.loading = true;
    this.loadingDns = true;

    // Fetch the domain details from the API using UUID
    this.domainsService
      .getDomainByUuid(this.domainUuid)
      .pipe(
        catchError((error) => {
          this.toast.error(error.message || 'Failed to load domain details. Please try again.');
          return of(null);
        }),
        finalize(() => {
          // Only set loading to false if we're not going to load DNS records
          // Check if domain exists before accessing dnsManaged
          if (!this.domain?.dnsManaged) {
            this.loading = false;
          }
          this.loadingDns = false;
        })
      )
      .subscribe((response) => {
        if (response) {
          this.domain = response;

          // Load DNS records for the domain only if DNS is managed by us
          if (this.domain.dnsManaged) {
            this.loadDnsRecords(); // This might reset loading, ensure finalize handles it correctly
          } else {
            this.loading = false; // Ensure loading stops if not managed
          }
        } else {
          this.loading = false; // Ensure loading stops if no response
        }
      });
  }

  // Add this handler method
  handleDomainVerified(): void {
    this.loadDomainDetails(); // Reload details when verification succeeds
  }

  loadDnsRecords(): void {
    if (!this.domain) return;

    this.domainsService
      .getDnsRecords(this.domain.name)
      .pipe(
        finalize(() => (this.loading = false)),
        catchError(() => {
          this.toast.error('Failed to load DNS records. Please try again.');
          return of({
            data: {
              api_records: [],
              db_records: [],
            },
          });
        })
      )
      .subscribe((response) => {
        if (this.domain) {
          // Only use db_records and keep all fields from InternalDnsRecord
          this.domain.dnsRecords = response.data.db_records;
          // The record_locked attribute comes from the server
        }
      });
  }

  showAddRecord(): void {
    this.recordForm.reset({
      type: 'A',
      name: '',
      ttl: 3600,
      content: '',
      priority: null,
    });
    this.isAddingNewRecord = true;
    this.editingRecordId = null;
    this.editingRecord = null;
  }

  editRecord(record: DnsRecord): void {
    // Prevent editing locked records
    if (record.record_locked) {
      return;
    }

    this.editingRecordId = record.id;
    this.editingRecord = record;
    this.isAddingNewRecord = false;
    this.recordForm.setValue({
      type: record.type,
      name: record.name,
      ttl: record.ttl,
      content: record.content,
      priority: record.priority || null,
    });
  }

  cancelEdit(): void {
    this.isAddingNewRecord = false;
    this.editingRecordId = null;
    this.editingRecord = null;
  }

  saveRecord(): void {
    if (this.recordForm.invalid || !this.domain) {
      return;
    }

    this.savingRecord = true;
    const formValue = this.recordForm.value;

    if (this.editingRecordId) {
      // Find the record to get its UUID
      const recordToUpdate = this.domain.dnsRecords.find((r) => r.id === this.editingRecordId);

      if (!recordToUpdate) {
        this.toast.error('Record not found');
        this.savingRecord = false;
        return;
      }

      // Update existing record
      const updateRequest: UpdateDnsRecordRequest = {
        uuid: recordToUpdate.uuid,
        name: formValue.name,
        type: formValue.type,
        content: formValue.content,
        ttl: formValue.ttl,
      };

      if (formValue.priority !== null) {
        updateRequest.priority = formValue.priority;
      }

      this.domainsService
        .updateDnsRecord(updateRequest)
        .pipe(finalize(() => (this.savingRecord = false)))
        .subscribe({
          next: (response) => {
            if (response?.data) {
              const index = this.domain?.dnsRecords.findIndex((r) => r.id === this.editingRecordId) ?? -1;

              if (index !== -1 && this.domain) {
                this.domain.dnsRecords[index] = response.data;
                this.toast.success('DNS record updated successfully');
                this.cancelEdit();
              }
            }
          },
          error: (error) => {
            const errorMessage = error.error?.message
              ? `Failed to update DNS record. ${error.error.message}`
              : 'Failed to update DNS record.';
            this.toast.error(errorMessage);
          },
        });
    } else {
      // Add new record
      const createRequest: CreateDnsRecordRequest = {
        domain_name: this.domain.name,
        name: formValue.name,
        type: formValue.type,
        content: formValue.content,
        ttl: formValue.ttl,
      };

      if (formValue.priority !== null) {
        createRequest.priority = formValue.priority;
      }

      this.domainsService
        .createDnsRecord(createRequest)
        .pipe(finalize(() => (this.savingRecord = false)))
        .subscribe({
          next: (response) => {
            if (response?.data) {
              this.domain?.dnsRecords.push(response.data);
              this.toast.success('DNS record added successfully');
              this.cancelEdit();
            }
          },
          error: (error) => {
            const errorMessage = error.error?.message
              ? `Failed to add DNS record. ${error.error.message}`
              : 'Failed to add DNS record.';
            this.toast.error(errorMessage);
          },
        });
    }
  }

  deleteRecord(record: DnsRecord): void {
    if (!this.domain || record.record_locked) {
      return;
    }

    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '400px',
      data: {
        name: `DNS Record (${record.type})`,
        action: 'Delete',
        additionalText: `This will delete the ${record.type} record with value <strong>${record.content}</strong>.`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true && this.domain) {
        this.deletingRecord = true;

        const deleteRequest: DeleteDnsRecordRequest = {
          uuid: record.uuid,
        };

        this.domainsService
          .deleteDnsRecord(deleteRequest)
          .pipe(
            finalize(() => (this.deletingRecord = false)),
            catchError((error) => {
              const errorMessage = error.error?.message
                ? `Failed to delete DNS record. ${error.error.message}`
                : 'Failed to delete DNS record. Please try again.';
              this.toast.error(errorMessage);
              return of(null);
            })
          )
          .subscribe((response) => {
            if (response?.data.success && this.domain) {
              // Remove the record from the local array
              this.domain.dnsRecords = this.domain.dnsRecords.filter((r) => r.id !== record.id);
              this.toast.success('DNS record deleted successfully');
            }
          });
      }
    });
  }

  getTtlLabel(ttl: number): string {
    const option = this.ttlOptions.find((o) => o.value === ttl);
    return option ? option.label : ttl.toString();
  }

  toggleAutoRenew(): void {
    if (this.domain) {
      const currentAutoRenew = this.domain.autoRenew;
      // Optimistically update the UI
      this.domain.autoRenew = !currentAutoRenew;

      this.domainsService
        .toggleDomainAutoRenew(this.domain.name, !currentAutoRenew)
        .pipe(
          catchError((error) => {
            console.error('Error toggling auto-renew:', error);
            // Revert the UI change if the API call fails
            this.domain!.autoRenew = currentAutoRenew;
            this.toast.error('Failed to update auto-renew setting. Please try again.');
            return of(null);
          })
        )
        .subscribe((response) => {
          if (response) {
            this.toast.success(`Auto-renew ${this.domain!.autoRenew ? 'enabled' : 'disabled'}`);
          }
        });
    }
  }

  getNextRenewalDate(): Date | null {
    if (!this.domain?.expiresAt) {
      return null;
    }

    const nextRenewalDate = new Date(this.domain.expiresAt);
    nextRenewalDate.setFullYear(nextRenewalDate.getFullYear() + 1);
    return nextRenewalDate;
  }

  deleteDomain(): void {
    if (!this.domain) return;

    // Construct the warning message conditionally
    let additionalMessage = '';

    if (this.domain.siteId) {
      additionalMessage += ' Deleting this domain will also unlink it from your Direct site.';
    }

    this.dialog
      .open(DialogDeleteGenericComponent, {
        data: {
          name: this.domain.name,
          action: 'Delete',
          additionalText: additionalMessage,
        },
        width: '400px',
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.domainsService
            .deleteDomain(this.domainUuid)
            .pipe(
              catchError((error) => {
                this.toast.error(error.message || 'Failed to delete domain. Please try again.');
                return of(null);
              })
            )
            .subscribe((response) => {
              if (response !== null) {
                this.toast.success('Domain deleted successfully');
                this.router.navigate(['/settings/domains']);
              }
            });
        }
      });
  }
}
